import { useCallback, useState } from 'react';
import { api } from '../api';

/**
 * useWitnessRoster — the single source of truth for the Witnesses tab roster.
 *
 * Before this hook the tab kept the roster, its mutations, and the
 * recently-merged list in separate stores that drifted apart — the
 * `RecentlyMergedPanel` had to be force-reloaded with a `refreshKey` counter
 * after an Arc cleanup applied merges. Here the roster (`accepted` /
 * `suggested`) AND the recently-merged ledger (`applied`) live together, so any
 * mutation that touches the merge ledger reloads both at once. Seam
 * consolidation step A (WITNESS_CLEANUP_WORKFLOW_PLAN §12 #5).
 *
 * UI state (which witness is active, multi-select, filters, the cleanup panel)
 * stays in the component — this hook owns DATA + the writes against it. The
 * `onActivate(id)` callback lets a mutation move the detail-pane focus (accept →
 * the new witness, merge → the survivor, split → the new card) without the hook
 * knowing about the component's `activeId`.
 *
 * Returns the lists, load/reload helpers, a `run` busy-wrapper, and the
 * optimistic mutations (`suggest`, `review`, `merge`, `split`, `moveRefs`,
 * `patch`, `undoMerge`). Mutations reconcile against the server's authoritative
 * response and fall back to a reload on failure.
 */
export default function useWitnessRoster(caseId, { onActivate } = {}) {
  const [accepted, setAccepted] = useState([]);
  const [suggested, setSuggested] = useState([]);
  const [applied, setApplied] = useState([]); // recently-merged ledger rows
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Build the query string by hand: the shared api client serializes an array
  // param as one comma-joined value, but the backend expects repeated
  // `review_state=…` params (FastAPI list[str] query).
  const loadRoster = useCallback(async () => {
    const qs = new URLSearchParams();
    qs.append('review_state', 'accepted');
    qs.append('review_state', 'suggested');
    const data = await api.get(`/cases/${caseId}/witnesses?${qs.toString()}`);
    const all = data.witnesses || [];
    setAccepted(all.filter((w) => w.review_state === 'accepted'));
    setSuggested(all.filter((w) => w.review_state === 'suggested'));
    return all;
  }, [caseId]);

  const loadApplied = useCallback(async () => {
    try {
      const data = await api.get(
        `/cases/${caseId}/witnesses/merges/recent`, { limit: 50 },
      );
      setApplied(data.merges || []);
    } catch { /* non-fatal — supplementary surface */ }
  }, [caseId]);

  // Reload everything touched by a merge/undo in one shot — the roster changed
  // AND the recently-merged ledger changed. This is what retires the refreshKey.
  const reloadAll = useCallback(async () => {
    await Promise.all([loadRoster(), loadApplied()]);
  }, [loadRoster, loadApplied]);

  const run = useCallback(async (fn) => {
    setBusy(true); setError('');
    try { await fn(); }
    catch (e) { setError(e.message || 'Action failed'); }
    finally { setBusy(false); }
  }, []);

  // Refresh / find new people. The endpoint persists suggestions as
  // review_state='suggested', so we just reload the roster afterward.
  const suggest = useCallback(() => run(async () => {
    await api.post(`/cases/${caseId}/witnesses/suggest?limit=60`);
    await loadRoster();
  }), [run, caseId, loadRoster]);

  // Optimistic review. accept/dismiss remove the card from the suggested list;
  // defer/undefer flip the `deferred` flag in place. Fire the review in the
  // background; resync on failure.
  const review = useCallback((witness, action) => {
    setError('');
    if (action === 'defer' || action === 'undefer') {
      const deferred = action === 'defer';
      setSuggested((s) => s.map((w) => (w.id === witness.id ? { ...w, deferred } : w)));
    } else {
      // accept / dismiss → leaves the suggested list entirely.
      setSuggested((s) => s.filter((w) => w.id !== witness.id));
      if (action === 'accept') {
        setAccepted((a) => [...a, { ...witness, review_state: 'accepted', deferred: false }]);
      }
    }
    Promise.resolve()
      .then(() => api.post(`/cases/${caseId}/witnesses/${witness.id}/review`, { action }))
      .then((updated) => {
        if (action === 'accept') {
          setAccepted((a) => a.map((w) => (w.id === witness.id ? updated : w)));
          onActivate?.(witness.id);
        } else if ((action === 'defer' || action === 'undefer') && updated) {
          // Reconcile with the server's authoritative row (keeps `deferred` honest).
          setSuggested((s) => s.map((w) => (w.id === witness.id ? updated : w)));
        }
      })
      .catch((e) => {
        setError(e.message || 'Action failed');
        loadRoster(); // server is the source of truth on failure
      });
  }, [caseId, loadRoster, onActivate]);

  // Merge a witness INTO a survivor. Works from a suggested card OR an accepted
  // roster row (the loser may live in either list — drop it from both
  // optimistically), then reloadAll to reconcile (the survivor's dossier
  // changed; the recently-merged ledger gained a row). `activeId` lets us move
  // focus off the just-merged loser onto the survivor.
  const merge = useCallback((loser, survivorId, activeId) => {
    if (!window.confirm(
      `Merge "${loser.display_name}" into this person?\n\n`
      + 'This combines them into one person everywhere in the case — Ask Arc, the '
      + 'chronology, and dossiers. You can undo it from "Recently merged".'
    )) return;
    setError('');
    setSuggested((s) => s.filter((w) => w.id !== loser.id));
    setAccepted((a) => a.filter((w) => w.id !== loser.id));
    if (activeId === loser.id) onActivate?.(survivorId);
    Promise.resolve()
      .then(() => api.post(`/cases/${caseId}/witnesses/merge`, {
        survivor_id: survivorId,
        loser_ids: [loser.id],
      }))
      .then(() => reloadAll())
      .catch((e) => {
        setError(e.message || 'Merge failed');
        reloadAll(); // server is the source of truth on failure
      });
  }, [caseId, reloadAll, onActivate]);

  // Bulk merge: combine a SELECTED SET of people into one — the "I already know
  // these are the same" action (no Arc, no size gate; a deterministic reversible
  // union). Survivor defaults to the most-documented (richest dossier stays
  // canonical); the rest fold in as losers in one call. Returns a promise.
  const mergeMany = useCallback((people, survivorId) => {
    const list = (people || []).filter(Boolean);
    if (list.length < 2) return Promise.resolve();
    const survivor = survivorId || [...list].sort(
      (a, b) => (b.evidence_count || 0) - (a.evidence_count || 0),
    )[0].id;
    const losers = list.filter((w) => w.id !== survivor);
    if (losers.length === 0) return Promise.resolve();
    setError('');
    const loserIds = new Set(losers.map((w) => w.id));
    setSuggested((s) => s.filter((w) => !loserIds.has(w.id)));
    setAccepted((a) => a.filter((w) => !loserIds.has(w.id)));
    onActivate?.(survivor);
    return api.post(`/cases/${caseId}/witnesses/merge`, {
      survivor_id: survivor,
      loser_ids: losers.map((w) => w.id),
    })
      .then(() => reloadAll())
      .catch((e) => {
        setError(e.message || 'Merge failed');
        reloadAll();
      });
  }, [caseId, reloadAll, onActivate]);

  // Split (un-merge): peel the chosen fragments off a witness into a NEW
  // accepted witness. The backend moves them atomically, so we reload rather
  // than mutate optimistically, then focus the new witness. Returns the promise
  // so the SplitSection can hold its button in a "Splitting…" state.
  const split = useCallback((sourceWitness, entityIds, displayName) => {
    setError('');
    return api.post(`/cases/${caseId}/witnesses/${sourceWitness.id}/split`, {
      entity_ids: entityIds,
      display_name: displayName || null,
    })
      .then((res) => loadRoster().then(() => {
        if (res?.new_witness?.id) onActivate?.(res.new_witness.id);
      }))
      .catch((e) => {
        setError(e.message || 'Split failed');
        loadRoster(); // server is the source of truth on failure
        throw e;
      });
  }, [caseId, loadRoster, onActivate]);

  // Move selected references into an EXISTING person — the references-rail
  // sibling of split (split peels strays into a NEW record; this rebinds them
  // to a person who already has one). One atomic POST; both dossiers changed,
  // so reload rather than mutate optimistically. Focus stays on the SOURCE —
  // the user is mid-cleanup on that card and wants to keep carving.
  const moveRefs = useCallback((sourceWitness, entityIds, targetWitnessId) => {
    setError('');
    return api.post(
      `/cases/${caseId}/witnesses/${sourceWitness.id}/references/move`,
      { entity_ids: entityIds, target_witness_id: targetWitnessId },
    )
      .then(() => loadRoster())
      .catch((e) => {
        setError(e.message || 'Move failed');
        loadRoster(); // server is the source of truth on failure
        throw e;
      });
  }, [caseId, loadRoster]);

  // Optimistic identity edit from the detail panel.
  const patch = useCallback((witness, patchObj) => {
    setError('');
    setAccepted((a) => a.map((w) => (w.id === witness.id ? { ...w, ...patchObj } : w)));
    Promise.resolve()
      .then(() => api.patch(`/cases/${caseId}/witnesses/${witness.id}`, patchObj))
      .then((updated) => setAccepted((a) => a.map((w) => (w.id === witness.id ? updated : w))))
      .catch((e) => {
        setError(e.message || 'Action failed');
        loadRoster();
      });
  }, [caseId, loadRoster]);

  // Reverse a whole merge OPERATION (recently-merged undo + per-entity
  // Separate both route here). Restores the merged-away people case-wide, then
  // reloadAll so the roster and the ledger both reflect the restore.
  const undoMerge = useCallback(async (opIds) => {
    await api.post(`/cases/${caseId}/witnesses/merges/undo`, {
      op_ids: opIds,
    });
    await reloadAll();
  }, [caseId, reloadAll]);

  return {
    accepted, suggested, applied,
    loading, setLoading, busy, error, setError,
    loadRoster, loadApplied, reloadAll, run,
    suggest, review, merge, mergeMany, split, moveRefs, patch, undoMerge,
  };
}
